/**
 * Тонкий fetch-обёртщик. Все admin-эндпоинты живут под /api/admin/*.
 * Аутентификация — session-cookie (HttpOnly), поэтому credentials='same-origin'.
 * 401 от любого admin-эндпоинта → редирект на /login (кроме самих auth-маршрутов).
 */
async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // Content-Type выставляем только при наличии тела — иначе Fastify падает на
  // application/json с пустым body (400 "Body cannot be empty").
  const headers: Record<string, string> = { ...((init.headers as Record<string, string>) ?? {}) };
  if (init.body !== undefined && init.body !== null && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(`/api${path}`, {
    ...init,
    headers,
    credentials: 'same-origin',
  });
  if (res.status === 401 && !path.startsWith('/auth/')) {
    if (window.location.pathname !== '/login') {
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.assign(`/login?next=${next}`);
    }
  }
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status} ${res.statusText}: ${text.slice(0, 200)}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

export type DashboardCardType = 'pipeline' | 'last_note' | 'ai_summary';

export interface DashboardPipeline {
  id: string;
  /** Итоговое имя для отображения: custom_title ?? default_title. */
  display_name: string;
  default_title: string;
  custom_title: string | null;
  status: 'active' | 'paused';
  events_24h: number;
  ok_24h: number;
  fail_24h: number;
  success_rate: number;
  p95_ms: number;
  last_error: string | null;
  last_event_at: string | null;
}

export interface DashboardLastNote {
  done_24h: number;
  skipped_24h: number;
  failed_24h: number;
  dead_24h: number;
  last_event_at: string | null;
  last_error: string | null;
  custom_title: string | null;
}

export interface DashboardAiSummary {
  enabled: boolean;
  configs_total: number;
  configs_active: number;
  last_run_at: string | null;
  runs_24h: number;
  fail_24h: number;
  leads_processed_24h: number;
  last_error: string | null;
  custom_title: string | null;
}

export interface DashboardGcal {
  integration_id: string;
  account_name: string;
  is_active: boolean;
  calendars_count: number;
  events_24h: number | null;
  events_7d: number | null;
  last_fetched_at: string | null;
  last_error: string | null;
}

export interface DashboardExternalSync {
  source: string;
  runs_24h: number;
  fail_24h: number;
  last_run_at: string | null;
  last_ok: boolean | null;
}

export interface DashboardExtras {
  last_note: DashboardLastNote | null;
  ai_summary: DashboardAiSummary | null;
  gcal: DashboardGcal[];
  external_sync: DashboardExternalSync[] | null;
}

export interface PipelineRow {
  id: string;
  display_name: string;
  status: 'active' | 'paused';
  kommo_account: { id: string; display_name: string; subdomain: string; slug: string };
  sheet_target: { id: string; display_name: string; spreadsheet_id: string; worksheet_name: string };
  pipeline_ids: number[];
  trigger_events: string[];
  field_mapping: Array<{
    entity?: 'lead' | 'contact' | 'company';
    kommo_field_id?: number | string;
    kommo_field_code?: string;
    sheet_column: string;
    type: string;
  }>;
  /**
   * Per-pipeline allow-list статусов: { "<kommo_pipeline_id>": [status_id, ...] }.
   * null/отсутствует → фильтра нет (все статусы разрешены).
   * Воронки нет в объекте → у неё нет фильтра.
   */
  status_filter: Record<string, number[]> | null;
  webhook_url: string;
}

export interface KommoStatus {
  id: number;
  name: string;
  sort: number;
  color: string;
}

export interface FullBackfillResponse {
  scanned: number;
  queued: number;
  skipped_by_status: number;
}

export interface KommoPipeline {
  id: number;
  name: string;
  is_main?: boolean;
}

export interface AccountRow {
  id: string;
  slug: string;
  display_name: string;
  subdomain: string;
  webhook_url: string;
}

export interface SheetRow {
  id: string;
  display_name: string;
  spreadsheet_id: string;
  worksheet_name: string;
}

export interface EventRow {
  id: string;
  pipeline_id: string;
  pipeline_name: string;
  kommo_event: string;
  kommo_entity_id: number | null;
  status: 'pending' | 'processing' | 'done' | 'failed' | 'dead';
  attempts: number;
  duration_ms: number | null;
  last_error: string | null;
  received_at: string;
  processed_at: string | null;
}

export interface KommoField {
  id: number;
  name: string;
  code: string | null;
  type: string;
}

export interface BillingWindowAgg {
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  cost_eur: number;
  client_usd: number;
  client_eur: number;
}

export interface BillingLine {
  connection_type: string;
  connection_id: string;
  label: string;
  /** Клиент (Kommo-аккаунт), к которому относится связка. */
  client_name: string;
  models: string[];
  /** LLM-расход за период (нули, если связка не тратит токены). */
  period: BillingWindowAgg;
  /** Заданная фикс-цена связки, EUR/мес. null = не задана. */
  price_eur_monthly: number | null;
  /** Наценка связки (× себестоимость LLM). Эффективная: своя или глобальный дефолт. */
  markup_x: number;
  /** Дата начала начисления связки, YYYY-MM-DD. null = не начисляется. */
  billed_since: string | null;
  /** Фикс, пропорционально периоду. */
  fixed_eur: number;
  fixed_usd: number;
  /** Итог связки = LLM клиенту + фикс. */
  total_eur: number;
  total_usd: number;
}

/** Биллинг клиента: долг на сегодня (всё в EUR). */
export interface ClientBilling {
  client_name: string;
  accrued_fixed_eur: number;
  accrued_llm_eur: number;
  accrued_eur: number;
  paid_eur: number;
  debt_eur: number;
}

export interface Payment {
  id: string;
  client_name: string;
  amount_eur: number;
  paid_on: string;
  note: string | null;
  created_at: string;
}

export interface BillingSummary {
  range: { from: string; to: string };
  proration: { days_in_range: number; factor: number };
  lines: BillingLine[];
  clients_billing: ClientBilling[];
  totals: {
    period: BillingWindowAgg;
    fixed_eur: number;
    fixed_usd: number;
    total_eur: number;
    total_usd: number;
    connections_count: number;
  };
  fx: { usd_to_eur: number; date: string; stale: boolean };
  markup: number;
}

export interface GcalCalendar {
  id: string;
  integration_id: string;
  calendar_id: string;
  display_name: string;
  sort_order: number;
  /** ID пользователя Kommo — для матчинга фильтра в Dashboard widget. null = общий. */
  kommo_user_id: string | null;
}

export interface GcalIntegration {
  id: string;
  kommo_account_id: string;
  account_display_name: string;
  keywords_viewing: string[];
  keywords_meeting: string[];
  lookahead_days: number;
  timezone: string;
  is_active: boolean;
  widget_slug: string;
  widget_url: string;
  calendars: GcalCalendar[];
}

export interface GcalPreviewByCalendar {
  calendar_id: string;
  display_name: string;
  ok: boolean;
  error: string | null;
  count: number;
}

export interface GcalPreview {
  period: 'day' | 'week' | 'month';
  total: number;
  by_calendar: GcalPreviewByCalendar[];
}

export interface AiSummaryConfig {
  id: string;
  kommo_account_id: string;
  display_name: string;
  pipeline_ids: number[];
  status_filter: Record<string, number[]> | null;
  status: 'active' | 'paused';
  activity_days_window: number;
  ai_marker: string;
  last_run_at: string | null;
  account_display_name: string;
  account_subdomain: string;
}

export interface AiSummaryRun {
  id: string;
  config_id: string;
  trigger: 'cron' | 'manual';
  started_at: string;
  finished_at: string | null;
  leads_total: number;
  leads_processed: number;
  leads_skipped: number;
  leads_failed: number;
  llm_tokens_input: number;
  llm_tokens_output: number;
  last_error: string | null;
}

export interface AiSummaryEvent {
  id: string;
  run_id: string;
  kommo_lead_id: string;
  status: 'done' | 'skipped' | 'failed';
  reason: string | null;
  note_id: string | null;
  llm_input_tokens: number | null;
  llm_output_tokens: number | null;
  duration_ms: number | null;
  processed_at: string;
}

export interface MetaLeadResponsible {
  id: string;
  connection_id: string;
  full_name: string;
  tg_username: string | null;
  /** Телефон ответственного — по нему бот подхватывает tg_user_id из shared-контакта. */
  phone: string | null;
  /** Числовой Telegram id (подхватывается ботом) — для надёжного @-пинга. */
  tg_user_id: string | null;
  status: 'active' | 'paused';
}

export interface MetaLeadRoute {
  id: string;
  connection_id: string;
  direction_key: string;
  label: string;
  form_ids: string[];
  campaign_ids: string[];
  message_thread_id: number | null;
  responsible_ids: string[];
  status: 'active' | 'paused';
}

export interface MetaLeadConnection {
  id: string;
  label: string;
  telegram_chat_id: string | null;
  status: 'active' | 'paused';
  client_name: string;
  responsibles: MetaLeadResponsible[];
  routes: MetaLeadRoute[];
}

export interface MetaLeadsOverview {
  connections: MetaLeadConnection[];
}

export type WidgetType =
  | 'gross-fee'
  | 'active-leads'
  | 'user-table'
  | 'calendar'
  | 'calendar-table';

export interface WidgetHealth {
  status: 'ok' | 'error' | 'inactive';
  http: number | null;
  latency_ms: number;
  error: string | null;
}

export interface WidgetRow {
  type: WidgetType;
  id: string;
  title: string;
  account_name: string;
  detail: string | null;
  is_active: boolean;
  widget_slug: string;
  html_url: string;
  json_url: string;
  created_at: string;
  updated_at: string | null;
  /** null, если health-check был отключён (?health=0). */
  health: WidgetHealth | null;
}
